/**
 * MessageCreatorsDialog — CM bulk WhatsApp send flow (Interakt integration).
 *
 * Additive-only: opens as a Dialog from BulkCampaignWorkspace, does not touch
 * pipeline / status / draft / live_url / mandate code.
 *
 * Flow: template pick → recipient checklist → variables & preview → send → progress → results
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  MessageSquare, Loader2, ChevronRight, ChevronLeft, CheckCircle2, XCircle,
  AlertTriangle, Send, Phone,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple template variable substitution: replaces {{key}} with values from `vars`.
const renderPreview = (template, vars) => {
  if (!template) return '';
  const placeholders = template.placeholders || [];
  const body = placeholders.map((k, i) => `${i + 1}. ${k}: ${vars[k] ?? '—'}`).join('\n');
  return `Template: ${template.display_name}\nCode name: ${template.code_name}\nLanguage: ${template.language_code}\n\nBody variables:\n${body}`;
};

export const MessageCreatorsDialog = ({ open, onOpenChange, campaignId, campaignName, brandName, creators }) => {
  const [step, setStep] = useState(1); // 1=template, 2=recipients, 3=variables/preview, 4=progress
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [variables, setVariables] = useState({});
  const [sending, setSending] = useState(false);
  const [bulkSendId, setBulkSendId] = useState(null);
  const [progress, setProgress] = useState(null); // { total, progress, counts, status }

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedTemplateId(null);
      setSelectedIds(new Set());
      setVariables({});
      setBulkSendId(null);
      setProgress(null);
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/whatsapp/templates`, { withCredentials: true });
      // Only show active + audience=creator (Message Creators flow)
      setTemplates(data.filter((t) => t.audience === 'creator'));
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  );

  // Auto-populate common variables when the template is picked
  useEffect(() => {
    if (!selectedTemplate) return;
    setVariables((prev) => ({
      creator_name: prev.creator_name ?? '',
      campaign_name: prev.campaign_name ?? campaignName ?? '',
      brand_name: prev.brand_name ?? brandName ?? '',
      date: prev.date ?? new Date().toISOString().slice(0, 10),
      ...prev,
    }));
  }, [selectedTemplate, campaignName, brandName]);

  // ---- Recipient selection ------------------------------------------------
  const eligibleCreators = useMemo(() => (creators || []), [creators]);
  const allSelected = eligibleCreators.length > 0 && selectedIds.size === eligibleCreators.length;

  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligibleCreators.map((c) => c.id)));
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- Send + poll --------------------------------------------------------
  const send = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one creator');
      return;
    }
    setSending(true);
    setStep(4);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/whatsapp/campaigns/${campaignId}/send-bulk`,
        {
          template_name: selectedTemplate.code_name,
          language_code: selectedTemplate.language_code || 'en',
          event_type: `manual_${selectedTemplate.event_type || 'cm_bulk'}`,
          recipient_row_ids: Array.from(selectedIds),
          common_variables: variables,
        },
        { withCredentials: true },
      );
      setBulkSendId(data.bulk_send_id);
      setProgress({ total: data.total, progress: 0, counts: {}, status: 'queued' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
      setStep(3);
    } finally {
      setSending(false);
    }
  };

  // Poll progress every 1s
  useEffect(() => {
    if (!bulkSendId || progress?.status === 'completed') return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/whatsapp/bulk-sends/${bulkSendId}`,
          { withCredentials: true },
        );
        setProgress(data);
      } catch (e) {
        // silent; will retry
      }
    }, 1000);
    return () => clearInterval(t);
  }, [bulkSendId, progress?.status]);

  const canNext = step === 1 ? !!selectedTemplate
    : step === 2 ? selectedIds.size > 0
    : true;

  const stepLabels = ['Template', 'Recipients', 'Preview & Send', 'Results'];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white dark:bg-[#0A0A0A] border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white" data-testid="message-creators-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F0F0F] dark:text-white">
            <MessageSquare size={16} className="text-[#25D366]" />
            Message Creators — WhatsApp
          </DialogTitle>
          <DialogDescription className="sr-only">Dialog content — use the controls below to complete the action.</DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-3" data-testid="step-indicator">
          {stepLabels.map((label, i) => (
            <div key={label} className="flex items-center gap-1 flex-1">
              <div
                className={`h-1 flex-1 rounded ${i + 1 <= step ? 'bg-[#25D366]' : 'bg-black/10 dark:bg-white/10'}`}
              />
              <div className={`text-[10px] font-mono uppercase tracking-wider ${i + 1 === step ? 'text-[#25D366]' : 'text-zinc-500'}`}>
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Step 1: Template */}
        {step === 1 && (
          <div className="space-y-2" data-testid="step-template">
            {loadingTemplates ? (
              <div className="py-8 text-center text-zinc-500 text-sm"><Loader2 className="animate-spin inline mr-2" size={14} />Loading…</div>
            ) : templates.length === 0 ? (
              <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 text-xs text-yellow-700 dark:text-yellow-200">
                <AlertTriangle size={14} className="inline mr-1" />
                No active WhatsApp templates. An Admin needs to activate a template first in Admin → WhatsApp Logs.
              </div>
            ) : (
              templates.map((t) => {
                const active = t.is_active;
                return (
                  <button
                    key={t.id}
                    disabled={!active}
                    onClick={() => setSelectedTemplateId(t.id)}
                    className={`w-full text-left rounded-xl p-3 border transition-colors ${
                      selectedTemplateId === t.id
                        ? 'border-[#25D366] bg-[#25D366]/5'
                        : active
                          ? 'border-black/[0.10] dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02] hover:bg-black/[0.04] dark:hover:bg-white/[0.05]'
                          : 'border-black/5 dark:border-white/5 bg-black/[0.01] dark:bg-white/[0.01] opacity-40 cursor-not-allowed'
                    }`}
                    data-testid={`template-${t.code_name}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold">{t.display_name}</div>
                        <div className="text-[11px] text-zinc-500 font-mono">
                          {t.code_name} · {t.language_code} · {(t.placeholders || []).length} variables
                        </div>
                      </div>
                      {!active && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/5 text-zinc-500 font-mono uppercase">
                          Inactive
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}

        {/* Step 2: Recipients */}
        {step === 2 && (
          <div className="space-y-2" data-testid="step-recipients">
            <div className="flex items-center justify-between p-2 bg-black/[0.03] dark:bg-white/[0.03] rounded-lg">
              <label className="text-xs font-mono uppercase tracking-wider flex items-center gap-2 cursor-pointer text-[#0F0F0F] dark:text-white">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all" />
                Select all ({selectedIds.size}/{eligibleCreators.length})
              </label>
            </div>
            <div className="max-h-[380px] overflow-y-auto space-y-1">
              {eligibleCreators.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                    selectedIds.has(c.id) ? 'bg-[#25D366]/10' : 'bg-black/[0.02] dark:bg-white/[0.02] hover:bg-black/[0.04] dark:hover:bg-white/[0.05]'
                  }`}
                  data-testid={`recipient-${c.id}`}
                >
                  <Checkbox
                    checked={selectedIds.has(c.id)}
                    onCheckedChange={() => toggleOne(c.id)}
                  />
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {c.creator_name?.[0]?.toUpperCase() || '·'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{c.creator_name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">
                      @{c.instagram_handle || '—'} · <Phone size={9} className="inline" /> phone on user profile
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Variables + Preview */}
        {step === 3 && selectedTemplate && (
          <div className="space-y-3" data-testid="step-preview">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              Variables ({selectedTemplate.placeholders?.length || 0})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(selectedTemplate.placeholders || []).map((key) => (
                <div key={key}>
                  <label className="text-[10px] font-mono uppercase block mb-1 text-[#0F0F0F] dark:text-white">{key}</label>
                  <Input
                    className="h-8 text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                    value={variables[key] ?? ''}
                    onChange={(e) => setVariables((v) => ({ ...v, [key]: e.target.value }))}
                    placeholder={key === 'creator_name' ? '(auto-filled per recipient)' : ''}
                    data-testid={`var-${key}`}
                  />
                </div>
              ))}
            </div>

            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mt-4">
              Preview
            </div>
            <Textarea
              readOnly
              value={renderPreview(selectedTemplate, variables)}
              className="border-[#25D366]/20 font-mono text-xs h-40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="preview-body"
            />
            <div className="text-[10px] text-zinc-500 font-mono">
              Sending to <span className="text-[#25D366]">{selectedIds.size}</span> creator(s).
              Their name will be auto-filled from each recipient row.
            </div>
          </div>
        )}

        {/* Step 4: Progress + results */}
        {step === 4 && (
          <div className="space-y-3" data-testid="step-results">
            <div className="text-sm font-mono text-[#4B5563] dark:text-zinc-300">
              Status: <span className="text-[#25D366] font-bold uppercase">{progress?.status || 'queued'}</span>
            </div>
            <div className="w-full h-2 bg-black/5 dark:bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#25D366] transition-all"
                style={{ width: `${Math.round(((progress?.progress || 0) / Math.max(1, progress?.total || 1)) * 100)}%` }}
                data-testid="progress-bar"
              />
            </div>
            <div className="text-[11px] font-mono text-zinc-400">
              {progress?.progress || 0} / {progress?.total || 0} processed
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="results-summary">
              <ResultTile label="Sent" value={progress?.counts?.sent || 0} color="#00C972" icon={CheckCircle2} testid="result-sent" />
              <ResultTile label="Failed" value={progress?.counts?.failed || 0} color="#FF3B30" icon={XCircle} testid="result-failed" />
              <ResultTile label="Skipped" value={progress?.counts?.skipped || 0} color="#FFB800" icon={AlertTriangle} testid="result-skipped" />
            </div>
            {progress?.status === 'completed' && (
              <div className="text-[11px] text-zinc-500 font-mono">
                Full delivery status is available under Admin → WhatsApp Logs.
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex justify-between gap-2 mt-4">
          <div className="flex gap-2">
            {step > 1 && step < 4 && (
              <Button
                variant="outline"
                onClick={() => setStep((s) => s - 1)}
                className="text-xs bg-white dark:bg-white/5 border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white hover:bg-black/5 dark:hover:bg-white/10"
                data-testid="btn-back"
              >
                <ChevronLeft size={12} className="mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step < 3 && (
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext}
                className="bg-[#25D366] hover:bg-[#20B85A] text-white text-xs disabled:opacity-40"
                data-testid="btn-next"
              >
                Next <ChevronRight size={12} className="ml-1" />
              </Button>
            )}
            {step === 3 && (
              <Button
                onClick={send}
                disabled={sending}
                className="bg-[#25D366] hover:bg-[#20B85A] text-white text-xs disabled:opacity-40"
                data-testid="btn-send"
              >
                {sending ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
                Send now
              </Button>
            )}
            {step === 4 && progress?.status === 'completed' && (
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-white dark:bg-white/5 hover:bg-black/5 dark:hover:bg-white/10 text-[#0F0F0F] dark:text-white border border-black/[0.10] dark:border-white/10 text-xs"
                data-testid="btn-close"
              >
                Close
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ResultTile = ({ label, value, color, icon: Icon, testid }) => (
  <div className="bg-black/[0.02] dark:bg-white/[0.03] border border-black/[0.10] dark:border-white/10 rounded-lg p-2 text-center" data-testid={testid}>
    <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500 font-mono mb-1">
      <Icon size={9} style={{ color }} /> {label}
    </div>
    <div className="text-xl font-bold font-mono" style={{ color }}>{value}</div>
  </div>
);
